import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { palette } from "../../../assets/styles/Palette";
import OrganismDisplayViewLinkLotation from "../components/organisms/OrganismDisplayViewLinkLotation";
import { viewDetailActivateLinkLotationRequest } from "../../../utils/indexedDB";

// API 로테이션용 다이렉트 링크 페이지
// 경로 예시: /AutodisplayViewLinkApiLotation/:viewId
const PageAutodisplayViewLinkApiLotation = () => {
  const navigate = useNavigate();
  const { path } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [lotationTimeSec, setLotationTimeSec] = useState(30); // 기본 30초
  const [lotationContents, setLotationContents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const timerRef = useRef(null);
  const isMountedRef = useRef(true);

  // 데이터 로드 함수 (초기/주기 갱신 공용)
  const fetchLotation = async (options = { silent: false }) => {
    if (!path) return;
    try {
      if (!options.silent) {
        setIsLoading(true);
        setError("");
      }
      const response = await viewDetailActivateLinkLotationRequest({ type: "custom", viewId: path });
      console.log("🚀 ~ fetchLotation ~ response:", response);
      const table = response?.lotationTable || response;
      if (!isMountedRef.current) return;

      const timeVal = parseInt(table?.lotationTime || table?.lotation_time || 60, 10);
      setLotationTimeSec(Number.isFinite(timeVal) ? timeVal : 30);

      const contents = Array.isArray(table?.lotationContents) ? table.lotationContents : [];
      setLotationContents(contents);
      console.log("🚀 ~ fetchLotation ~ contents:", contents);
      setCurrentIndex(0);
    } catch (e) {
      console.error(e);
      if (!isMountedRef.current) return;
      setError("데이터 로드 중 오류가 발생했습니다.");
    } finally {
      if (!isMountedRef.current) return;
      if (!options.silent) setIsLoading(false);
    }
  };

  // 마운트/언마운트 플래그 및 최초 로드
  useEffect(() => {
    isMountedRef.current = true;
    fetchLotation({ silent: false });
    return () => {
      isMountedRef.current = false;
    };
  }, [path]);

  // 10분 간격 데이터 새로고침 타이머
  useEffect(() => {
    if (!path) return;
    const id = setInterval(() => {
      fetchLotation({ silent: true });
    }, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, [path]);

  // 로테이션 타이머 (초기 즉시 실행 + 간격 회전)
  useEffect(() => {
    if (!lotationContents.length) return;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // 첫 로드 즉시 한 번 세팅
    setCurrentIndex((prev) => (prev >= lotationContents.length ? 0 : prev));
    const intervalMs = Math.max(1, parseInt(lotationTimeSec, 10)) * 1000;
    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % lotationContents.length);
    }, intervalMs);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [lotationContents, lotationTimeSec]);

  // 현재 표시할 콘텐츠 파생
  const currentContent = useMemo(() => {
    if (!lotationContents.length) return null;
    return lotationContents[currentIndex] || null;
  }, [lotationContents, currentIndex]);

  // 현재 콘텐츠에서 뷰어로 전달할 데이터 준비
  const { textData, sensorSerialData, locationData, themeMode } = useMemo(() => {
    if (!currentContent) {
      return { textData: "", sensorSerialData: null, locationData: null, themeMode: "light" };
    }

    const contentData = currentContent.contentData || {};
    console.log("🚀 ~ currentContent ~ contentData:", contentData);
    const dataType = contentData.dataType;
    const isSensor = dataType === "sensor";

    const sensor = isSensor
      ? {
          title: contentData.title || "",
          serialCode: contentData.serialCode || "",
        }
      : null;

    const location = contentData.selectedRegion || contentData.selectedDistricts
      ? {
          selectedRegion: contentData.selectedRegion,
          selectedDistricts: contentData.selectedDistricts,
        }
      : null;

    // 디자인 컨셉에서 테마 모드 유추 (없으면 light)
    const concept = currentContent.designConcept || currentContent.designSelectedConcept || [];
    const conceptFirst = Array.isArray(concept) ? concept[0] : concept;
    const mode = conceptFirst === "다크 모드" || conceptFirst === "dark" ? "dark" : "light";

    return {
      textData: currentContent?.selectedMessage?.text || currentContent?.textData || "",
      sensorSerialData: sensor,
      locationData: location,
      themeMode: mode,
    };
  }, [currentContent]);

  if (isLoading) {
    return (
      <FullScreenContainer>
        <LoadingText>로딩 중...</LoadingText>
      </FullScreenContainer>
    );
  }

  if (error) {
    return (
      <FullScreenContainer>
        <ErrorText>{error}</ErrorText>
      </FullScreenContainer>
    );
  }

  if (!path) {
    return null;
  }

  if (!currentContent) {
    return (
      <FullScreenContainer>
        <EmptyText>편성된 콘텐츠가 없습니다.</EmptyText>
      </FullScreenContainer>
    );
  }

  return (
    <AutodisplayContainer>
      <AnimationWrapper>
        <OrganismDisplayViewLinkLotation
          viewId={currentContent.viewId || path}
          textData={textData}
          sensorSerialData={sensorSerialData}
          locationData={locationData}
          themeMode={themeMode}
        />
      </AnimationWrapper>
    </AutodisplayContainer>
  );
};

export default PageAutodisplayViewLinkApiLotation;

const FullScreenContainer = styled.div`
  width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 0;
  background: #1f1f1f;
  color: ${palette.white};
`;

const LoadingText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${palette.gray200};
`;

const ErrorText = styled(LoadingText)`
  color: ${palette.error};
`;

const EmptyText = styled(LoadingText)`
  color: ${palette.gray300};
`;

const AutodisplayContainer = styled.div`
    width: 100vw;
    height: 100vh;
    margin: 0;
    padding: 0;
    background: #1f1f1f;
    overflow: hidden;
    position: fixed;
    top: 0;
    left: 0;
`;

// EduDesktopLotation과 동일한 슬라이드 인 애니메이션 적용
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const AnimationWrapper = styled.div`
  width: 100%;
  height: 100%;
  animation: ${slideIn} 0.5s ease-out;
`;

